/**
 * API client for the school lookup typeahead endpoint.
 *
 * Contract (WI-23b): GET /api/v1/schools/lookup?q=<query>&limit=<n>
 * Returns a SchoolLookupResult array.
 *
 * The function returns an empty array (not a thrown error) when the server
 * responds with HTTP 200 and an empty array body. It throws on non-2xx responses
 * so the component can render an error state.
 */

export interface MatchedAlias {
  alias: string;
  kind: string;
}

export interface SchoolLookupResult {
  slug: string;
  name: string;
  division: string;
  region: string;
  state: string;
  /** Non-null when the query matched via school_aliases.alias rather than schools.name. */
  matched_alias: MatchedAlias | null;
}

/**
 * Fetch school lookup typeahead results.
 *
 * @param query - Search string. Minimum 2 characters (API enforces; shorter → []).
 * @param limit - Max results. Default 8, API clamps to 20.
 * @param signal - Optional AbortSignal for request cancellation.
 */
export async function fetchSchoolLookup(
  query: string,
  limit?: number,
  signal?: AbortSignal,
): Promise<SchoolLookupResult[]> {
  const params = new URLSearchParams({ q: query });
  if (limit !== undefined) {
    params.set('limit', String(limit));
  }

  const response = await fetch(`/api/v1/schools/lookup?${params.toString()}`, {
    signal,
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`Lookup failed: HTTP ${response.status}`);
  }

  const data: unknown = await response.json();
  if (!Array.isArray(data)) {
    return [];
  }
  return data as SchoolLookupResult[];
}
